import * as THREE from 'three';
import type { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { modelLoader } from '../models/builder.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { Material, PieceInfo, PiecePose, PlacementSpace } from '@vrlearning/lesson-schema';
import { blob, grain } from '../worlds/patterns.js';
import { MATERIAL_LOOKS } from './materials.js';
import { NamePlates, PLATE, plateVisible, type PlateEntry } from './plates.js';

/**
 * The pupils' furniture in the villa (ADR-037; spec "Pieces"): each piece's plain GLB, fetched by
 * its signed URL and parsed with the plain GLTFLoader (no decoders: the CSP allows no WebAssembly),
 * given box-projected UVs for the wood grain, and drawn as one mesh -- one draw call -- in one of
 * nine shared materials. Pieces arrive one per frame, so forty never hitch; until then, and for good
 * if its file cannot be had, a piece is a flat grey footprint under its name plate.
 *
 * How a piece looks says who has it: pointed at and free, a faint white; held by this pupil, a
 * brighter white; held by someone else, amber (the handles' "this moves" colour). The headset never
 * shows who. A held piece casts no shadow -- a soft blob lies under it -- so carrying never needs the
 * shadow map drawn again; a standing one casts and receives.
 */

/** Who has a piece, as this headset sees it. `pending`: this pupil asked, the server has not said. */
export type PieceLook = 'free' | 'pending' | 'mine' | 'taken';

/** The emissive highlights, as the spec gives them. */
export const HIGHLIGHT = {
  pointed: { colour: 0xffffff, intensity: 0.12 },
  mine: { colour: 0xffffff, intensity: 0.2 },
  taken: { colour: 0xf59e0b, intensity: 0.35 },
  /** Amber-white while the grab waits for the server's answer, pulsing between these. */
  pending: { colour: 0xffd9a0, low: 0.1, high: 0.4, hz: 2.5 },
} as const;

type Highlight = keyof typeof HIGHLIGHT | 'none';

/**
 * UVs projected from a box (spec: "by the dominant axis of its normal, one texture repeat per
 * metre, in the piece's own frame"): a face looking along x takes (z, y), along y (x, z), along z
 * (x, y). The grain runs along u.
 */
export function addBoxUvs(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  const position = geometry.getAttribute('position');
  const normal = geometry.getAttribute('normal');
  const uv = new Float32Array(position.count * 2);
  for (let i = 0; i < position.count; i++) {
    const ax = Math.abs(normal.getX(i));
    const ay = Math.abs(normal.getY(i));
    const az = Math.abs(normal.getZ(i));
    const x = position.getX(i);
    const y = position.getY(i);
    const z = position.getZ(i);
    if (ax >= ay && ax >= az) uv.set([z, y], i * 2);
    else if (ay >= az) uv.set([x, z], i * 2);
    else uv.set([x, y], i * 2);
  }
  geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return geometry;
}

/**
 * A piece's GLB as one geometry: its meshes merged in the file's own frame, normals where the file
 * has none, box UVs added. Plain glTF only, as the server writes it.
 */
export async function parsePiece(
  bytes: ArrayBuffer,
  // Images, if a piece ever has any, through `<img>`: the CSP refuses a blob: fetch (builder.ts).
  loader: GLTFLoader = modelLoader(),
): Promise<THREE.BufferGeometry> {
  const gltf = await loader.parseAsync(bytes, '');
  gltf.scene.updateMatrixWorld(true);
  const parts: THREE.BufferGeometry[] = [];
  gltf.scene.traverse((node) => {
    if (!(node instanceof THREE.Mesh)) return;
    const geometry = (node.geometry as THREE.BufferGeometry).clone().applyMatrix4(node.matrixWorld);
    for (const name of Object.keys(geometry.attributes)) {
      if (name !== 'position' && name !== 'normal') geometry.deleteAttribute(name);
    }
    if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();
    parts.push(geometry);
  });
  if (parts.length === 0) throw new Error('the file holds no mesh');
  const merged = parts.length === 1 ? parts[0]! : mergeGeometries(parts, false);
  if (!merged) throw new Error('the meshes could not be merged');
  addBoxUvs(merged);
  merged.computeBoundingBox();
  merged.computeBoundingSphere();
  return merged;
}

/**
 * The nine materials, shared by every piece, and their highlighted variants made as needed. Double-
 * sided: a pupil's mesh may be open or wound inside out, and three lights a back face by its
 * flipped normal.
 */
export class FurnitureMaterials {
  readonly #grain: THREE.DataTexture;
  readonly #cache = new Map<string, THREE.MeshStandardMaterial>();
  #pulse = 0;

  constructor() {
    this.#grain = grain();
  }

  get(material: Material, highlight: Highlight = 'none'): THREE.MeshStandardMaterial {
    const key = `${material}:${highlight}`;
    let made = this.#cache.get(key);
    if (!made) {
      const look = MATERIAL_LOOKS[material];
      made = new THREE.MeshStandardMaterial({
        color: look.colour,
        roughness: look.roughness,
        metalness: look.metalness,
        side: THREE.DoubleSide,
        ...(look.grain ? { map: this.#grain } : {}),
      });
      made.name = `furniture:${key}`;
      if (highlight === 'pending') {
        made.emissive.set(HIGHLIGHT.pending.colour);
        made.emissiveIntensity = this.#pulse;
      } else if (highlight !== 'none') {
        made.emissive.set(HIGHLIGHT[highlight].colour);
        made.emissiveIntensity = HIGHLIGHT[highlight].intensity;
      }
      this.#cache.set(key, made);
    }
    return made;
  }

  /** The pending pulse at `seconds`. */
  pulse(seconds: number): void {
    const { low, high, hz } = HIGHLIGHT.pending;
    this.#pulse = low + (high - low) * (0.5 + 0.5 * Math.sin(seconds * Math.PI * 2 * hz));
    for (const [key, material] of this.#cache) {
      if (key.endsWith(':pending')) material.emissiveIntensity = this.#pulse;
    }
  }

  /** The grain's GPU bytes, with mips, for the budget. */
  static get grainBytes(): number {
    return 512 * 512 * 4 * (4 / 3);
  }

  dispose(): void {
    for (const material of this.#cache.values()) material.dispose();
    this.#cache.clear();
    this.#grain.dispose();
  }
}

type Node = {
  readonly info: PieceInfo;
  readonly plate: number;
  readonly group: THREE.Group;
  readonly footprint: THREE.Mesh;
  mesh: THREE.Mesh | undefined;
  pose: PiecePose;
  look: PieceLook;
  failed: boolean;
  /** Its plate this frame, refilled rather than made anew. */
  readonly entry: PlateEntry;
  /** Its bounds in the world, for a ray's first, cheap test; undefined when it has moved. */
  box: THREE.Box3 | undefined;
};

export type FurnitureViewOptions = {
  /** A piece's file by its signed URL (the service worker caches /v1/media/ by path). */
  readonly fetchBytes: (url: string) => Promise<ArrayBuffer>;
  readonly parse?: (bytes: ArrayBuffer) => Promise<THREE.BufferGeometry>;
  /** A piece whose file could not be fetched or read. */
  readonly onMissing?: (count: number) => void;
  /** A piece's shape already parsed (the viewer's): used, copied, instead of fetching its file. */
  readonly known?: (pieceId: string) => THREE.BufferGeometry | undefined;
  /** Where the world's pieces start: their name plates stay shown there. */
  readonly space: PlacementSpace;
};

const HIDDEN = new THREE.Matrix4().makeScale(0, 0, 0);
const DEG = Math.PI / 180;

export class FurnitureView {
  readonly root = new THREE.Group();
  readonly plates: NamePlates;
  readonly materials = new FurnitureMaterials();
  readonly #options: FurnitureViewOptions;
  readonly #nodes = new Map<string, Node>();
  readonly #arrived: { id: string; geometry: THREE.BufferGeometry }[] = [];
  readonly #blobs: THREE.InstancedMesh;
  readonly #blobTexture: THREE.DataTexture;
  readonly #footprintMaterial = new THREE.MeshBasicMaterial({
    color: 0x9a9a9a,
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
  });
  readonly #raycaster = new THREE.Raycaster();
  // Kept, not made each frame: the blobs' matrix parts, the plates' entries, the ray's targets.
  readonly #matrix = new THREE.Matrix4();
  readonly #blobAt = new THREE.Vector3();
  readonly #blobScale = new THREE.Vector3();
  readonly #still = new THREE.Quaternion();
  readonly #entries: PlateEntry[] = [];
  #entriesStale = true;
  readonly #hit = new THREE.Vector3();
  readonly #candidates: { node: Node; distance: number }[] = [];
  #pointed: string | undefined;
  #missing = 0;
  #disposed = false;

  constructor(options: FurnitureViewOptions, capacity = 40) {
    this.#options = options;
    this.root.name = 'furniture';
    this.plates = new NamePlates(capacity);
    this.root.add(this.plates.mesh);
    this.#blobTexture = blob();
    this.#blobs = new THREE.InstancedMesh(
      new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({
        map: this.#blobTexture,
        transparent: true,
        depthWrite: false,
        color: 0x000000,
      }),
      capacity,
    );
    this.#blobs.name = 'furniture-blobs';
    this.#blobs.frustumCulled = false;
    for (let i = 0; i < capacity; i++) this.#blobs.setMatrixAt(i, HIDDEN);
    this.root.add(this.#blobs);
  }

  get ids(): string[] {
    return [...this.#nodes.keys()];
  }

  has(id: string): boolean {
    return this.#nodes.has(id);
  }

  info(id: string): PieceInfo | undefined {
    return this.#nodes.get(id)?.info;
  }

  pose(id: string): PiecePose | undefined {
    return this.#nodes.get(id)?.pose;
  }

  look(id: string): PieceLook | undefined {
    return this.#nodes.get(id)?.look;
  }

  /** Pieces whose files could not be had. */
  get missing(): number {
    return this.#missing;
  }

  /** How many pieces are drawn as themselves, not as footprints. */
  get loaded(): number {
    return [...this.#nodes.values()].filter((node) => node.mesh !== undefined).length;
  }

  /** A piece of the set, standing at `pose`; its file fetched from `url` when there is one. */
  add(info: PieceInfo, pose: PiecePose, url: string | undefined): void {
    if (this.#nodes.has(info.id)) return;
    const used = new Set([...this.#nodes.values()].map((node) => node.plate));
    let plate = 0;
    while (used.has(plate)) plate++;
    const group = new THREE.Group();
    group.name = `piece:${info.id}`;
    const footprint = new THREE.Mesh(
      new THREE.PlaneGeometry(info.size[0], info.size[2]).rotateX(-Math.PI / 2),
      this.#footprintMaterial,
    );
    footprint.position.y = 0.004;
    footprint.userData['pieceId'] = info.id;
    group.add(footprint);
    const node: Node = {
      info,
      plate,
      group,
      footprint,
      mesh: undefined,
      pose,
      look: 'free',
      failed: false,
      entry: { index: plate, shown: false, taken: false, at: new THREE.Vector3() },
      box: undefined,
    };
    this.#nodes.set(info.id, node);
    this.#entriesStale = true;
    this.root.add(group);
    this.plates.set(plate, info.title);
    this.#place(node);
    const known = this.#options.known?.(info.id);
    if (known) {
      this.#arrived.push({ id: info.id, geometry: known.clone() });
      return;
    }
    if (url === undefined) {
      this.#fail(node);
      return;
    }
    const parse = this.#options.parse ?? ((bytes: ArrayBuffer) => parsePiece(bytes));
    this.#options
      .fetchBytes(url)
      .then(parse)
      .then(
        (geometry) => {
          if (this.#disposed || this.#nodes.get(info.id) !== node) geometry.dispose();
          else this.#arrived.push({ id: info.id, geometry });
        },
        (error: unknown) => {
          console.warn(`furniture: piece ${info.id} could not be loaded`, error);
          if (this.#nodes.get(info.id) === node) this.#fail(node);
        },
      );
  }

  remove(id: string): void {
    const node = this.#nodes.get(id);
    if (!node) return;
    this.#nodes.delete(id);
    this.#entriesStale = true;
    node.group.removeFromParent();
    node.mesh?.geometry.dispose();
    node.footprint.geometry.dispose();
    this.plates.clear(node.plate);
    if (this.#pointed === id) this.#pointed = undefined;
  }

  setPose(id: string, pose: PiecePose): void {
    const node = this.#nodes.get(id);
    if (!node) return;
    const materialChanged = pose.material !== node.pose.material;
    node.pose = pose;
    this.#place(node);
    if (materialChanged) this.#dress(node);
  }

  setLook(id: string, look: PieceLook): void {
    const node = this.#nodes.get(id);
    if (!node || node.look === look) return;
    node.look = look;
    this.#dress(node);
  }

  /** The piece a pupil points at, lit faintly while free. */
  setPointed(id: string | undefined): void {
    if (id === this.#pointed) return;
    const before = this.#pointed === undefined ? undefined : this.#nodes.get(this.#pointed);
    this.#pointed = id;
    if (before) this.#dress(before);
    const now = id === undefined ? undefined : this.#nodes.get(id);
    if (now) this.#dress(now);
  }

  /**
   * The nearest piece along a ray within `reach` metres. Each piece's world box is tried first and
   * only the pieces the ray meets are raycast, nearest box first, their triangles only while one of
   * them could still be nearer than the best hit.
   */
  along(ray: THREE.Ray, reach: number): { id: string; distance: number } | undefined {
    const candidates = this.#candidates;
    candidates.length = 0;
    for (const node of this.#nodes.values()) {
      const box = this.#boxOf(node);
      if (!ray.intersectBox(box, this.#hit)) continue;
      const distance = this.#hit.distanceTo(ray.origin);
      if (distance <= reach) candidates.push({ node, distance });
    }
    if (candidates.length === 0) return undefined;
    candidates.sort((a, b) => a.distance - b.distance);
    this.#raycaster.set(ray.origin, ray.direction);
    this.#raycaster.far = reach;
    let best: { id: string; distance: number } | undefined;
    for (const { node, distance } of candidates) {
      if (best && distance > best.distance) break;
      const hit = this.#raycaster.intersectObject(node.mesh ?? node.footprint, false)[0];
      if (hit && (!best || hit.distance < best.distance))
        best = { id: node.info.id, distance: hit.distance };
    }
    return best;
  }

  /** A piece's bounds in the world, made again only after it moved or arrived. */
  #boxOf(node: Node): THREE.Box3 {
    if (!node.box) {
      const target = node.mesh ?? node.footprint;
      target.updateWorldMatrix(true, false);
      const geometry = target.geometry;
      if (!geometry.boundingBox) geometry.computeBoundingBox();
      node.box = geometry.boundingBox!.clone().applyMatrix4(target.matrixWorld);
      // A footprint is flat: give it a little height, so a ray from the side can find it.
      node.box.max.y = Math.max(node.box.max.y, 0.05);
    }
    return node.box;
  }

  /**
   * Once a frame: at most one arrived piece put in the scene, the pending pulse, the blobs under
   * held pieces, and the plates facing `camera`.
   */
  update(seconds: number, camera: THREE.Camera): void {
    const next = this.#arrived.shift();
    if (next) this.#put(next.id, next.geometry);
    this.materials.pulse(seconds);
    let blob = 0;
    if (this.#entriesStale) {
      this.#entries.length = 0;
      for (const node of this.#nodes.values()) this.#entries.push(node.entry);
      this.#entriesStale = false;
    }
    for (const node of this.#nodes.values()) {
      const held = node.look === 'mine' || node.look === 'taken';
      if (held && blob < this.#blobs.count) {
        const size = Math.max(node.info.size[0], node.info.size[2]) * 1.25;
        this.#matrix.compose(
          this.#blobAt.set(node.pose.at[0], (node.pose.y ?? 0) + 0.006, node.pose.at[1]),
          this.#still,
          this.#blobScale.set(size, 1, size),
        );
        this.#blobs.setMatrixAt(blob++, this.#matrix);
      }
      const entry = node.entry;
      entry.at.set(
        node.pose.at[0],
        (node.pose.y ?? 0) + node.info.size[1] + PLATE.lift + PLATE.height / 2,
        node.pose.at[1],
      );
      entry.taken = node.look === 'taken';
      entry.shown = plateVisible(
        node.pose.at,
        this.#pointed === node.info.id || node.look !== 'free',
        this.#options.space,
      );
    }
    for (let i = blob; i < this.#blobs.count; i++) this.#blobs.setMatrixAt(i, HIDDEN);
    this.#blobs.instanceMatrix.needsUpdate = true;
    this.plates.update(this.#entries, camera);
  }

  #put(id: string, geometry: THREE.BufferGeometry): void {
    const node = this.#nodes.get(id);
    if (!node) {
      geometry.dispose();
      return;
    }
    const mesh = new THREE.Mesh(geometry, this.materials.get(node.pose.material));
    mesh.name = `piece-mesh:${id}`;
    mesh.userData['pieceId'] = id;
    node.mesh = mesh;
    node.box = undefined;
    node.group.add(mesh);
    node.footprint.visible = false;
    this.#dress(node);
  }

  #fail(node: Node): void {
    if (node.failed) return;
    node.failed = true;
    this.#missing++;
    this.#options.onMissing?.(this.#missing);
  }

  #place(node: Node): void {
    // Its underside at the pose's height: on a table's top, or hung against a wall.
    node.group.position.set(node.pose.at[0], node.pose.y ?? 0, node.pose.at[1]);
    node.group.rotation.set(0, node.pose.turn * DEG, 0);
    node.box = undefined;
  }

  #dress(node: Node): void {
    const mesh = node.mesh;
    if (!mesh) return;
    const highlight: Highlight =
      node.look === 'mine'
        ? 'mine'
        : node.look === 'taken'
          ? 'taken'
          : node.look === 'pending'
            ? 'pending'
            : this.#pointed === node.info.id
              ? 'pointed'
              : 'none';
    mesh.material = this.materials.get(node.pose.material, highlight);
    const standing = node.look === 'free' || node.look === 'pending';
    mesh.castShadow = standing;
    mesh.receiveShadow = true;
  }

  dispose(): void {
    this.#disposed = true;
    for (const id of [...this.#nodes.keys()]) this.remove(id);
    for (const arrived of this.#arrived.splice(0)) arrived.geometry.dispose();
    this.materials.dispose();
    this.plates.dispose();
    this.#footprintMaterial.dispose();
    this.#blobTexture.dispose();
    this.#blobs.geometry.dispose();
    (this.#blobs.material as THREE.Material).dispose();
    this.#blobs.dispose();
    this.root.removeFromParent();
  }
}
